import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  MoreVert,
  Search,
  FilterList,
  Block,
  CheckCircle,
  Person,
  Phone,
  LocationOn,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';

interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  profileImageUrl?: string;
  location?: {
    address: string;
    city: string;
    country: string;
  };
  isActive: boolean;
  lastLogin: string;
  createdAt: string;
  favoritesCount: number;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'activate' | 'deactivate' | 'delete'>('activate');
  const [isLoading, setIsLoading] = useState(true);

  const filterUsers = useCallback(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.isActive === (statusFilter === 'active'));
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.success) {
        // Transform backend data to frontend format
        const transformedUsers: User[] = data.data.users.map((user: any) => ({
          id: user._id,
          name: user.name,
          email: user.email,
          phone: user.phone || '',
          location: user.location ? {
            address: user.location.address || '',
            city: user.location.city || '',
            country: user.location.country || '',
          } : undefined,
          isActive: user.isActive,
          lastLogin: user.lastLogin,
          createdAt: user.createdAt,
          favoritesCount: user.favorites ? user.favorites.length : 0,
        }));
        setUsers(transformedUsers);
      } else {
        // Fallback to mock data if API fails
        const mockUsers: User[] = [
        {
          id: '1',
          name: 'John Doe',
          email: 'john@example.com',
          phone: '+1 (555) 123-4567',
          isActive: true,
          lastLogin: '2024-01-15T10:30:00Z',
          createdAt: '2024-01-01T00:00:00Z',
          favoritesCount: 12,
          location: {
            address: '123 Main St',
            city: 'New York',
            country: 'USA',
          },
        },
        {
          id: '2',
          name: 'Jane Smith',
          email: 'jane@example.com',
          phone: '+1 (555) 987-6543',
          isActive: true,
          lastLogin: '2024-01-14T15:45:00Z',
          createdAt: '2024-01-02T00:00:00Z',
          favoritesCount: 8,
          location: {
            address: '456 Oak Ave',
            city: 'Los Angeles',
            country: 'USA',
          },
        },
        {
          id: '3',
          name: 'Mike Johnson',
          email: 'mike@example.com',
          isActive: false,
          lastLogin: '2024-01-10T09:20:00Z',
          createdAt: '2024-01-03T00:00:00Z',
          favoritesCount: 5,
        },
        {
          id: '4',
          name: 'Sarah Wilson',
          email: 'sarah@example.com',
          phone: '+1 (555) 456-7890',
          isActive: true,
          lastLogin: '2024-01-15T14:15:00Z',
          createdAt: '2024-01-04T00:00:00Z',
          favoritesCount: 15,
          location: {
            address: '789 Pine St',
            city: 'Chicago',
            country: 'USA',
          },
        },
      ];
      
      setUsers(mockUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Set mock data for demo
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserAction = (user: User, action: 'activate' | 'deactivate' | 'delete') => {
    setSelectedUser(user);
    setActionType(action);
    setConfirmDialog(true);
    setActionMenuAnchor(null);
  };

  const confirmAction = async () => {
    if (!selectedUser) return;

    try {
      let endpoint = '';
      let method = 'PUT';

      const baseUrl = process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin';
      
      switch (actionType) {
        case 'activate':
          endpoint = `${baseUrl}/users/${selectedUser.id}/activate`;
          break;
        case 'deactivate':
          endpoint = `${baseUrl}/users/${selectedUser.id}/deactivate`;
          break;
        case 'delete':
          endpoint = `${baseUrl}/users/${selectedUser.id}`;
          method = 'DELETE';
          break;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(endpoint, { 
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        // Update local state
        if (actionType === 'delete') {
          setUsers(users.filter(user => user.id !== selectedUser.id));
        } else {
          setUsers(users.map(user =>
            user.id === selectedUser.id
              ? { ...user, isActive: actionType === 'activate' }
              : user
          ));
        }
      }
    } catch (error) {
      console.error('Error performing action:', error);
    } finally {
      setConfirmDialog(false);
      setSelectedUser(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'User',
      width: 180,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Phone sx={{ mr: 1, fontSize: 16 }} />
          {params.value || 'N/A'}
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationOn sx={{ mr: 1, fontSize: 16 }} />
          {params.value ? `${params.value.city}, ${params.value.country}` : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'favoritesCount',
      headerName: 'Favorites',
      width: 100,
      type: 'number',
    },
    {
      field: 'lastLogin',
      headerName: 'Last Login',
      width: 150,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 150,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MoreVert />}
          label="More"
          onClick={(event) => {
            setActionMenuAnchor(event.currentTarget);
            setSelectedUser(params.row);
          }}
        />,
      ],
    },
  ];

  return (
    <Box>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
      >
        User Management
      </Typography>
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ 
          mb: { xs: 3, md: 4 },
          fontSize: { xs: '0.875rem', sm: '1rem' },
        }}
      >
        Manage and monitor all registered users on the platform.
      </Typography>

      {/* Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 2 }, alignItems: 'center' }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 250px' }, minWidth: { xs: '100%', sm: '250px' } }}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 200px' }, minWidth: { xs: 'calc(50% - 8px)', sm: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 150px' }, minWidth: { xs: 'calc(50% - 8px)', sm: '150px' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                fullWidth
              >
                Filters
              </Button>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: '200px' } }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" sx={{ flex: 1 }}>
                  Export
                </Button>
                <Button variant="outlined" size="small" sx={{ flex: 1 }}>
                  Bulk Actions
                </Button>
              </Box>
            </Box>
            </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card sx={{ 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              height: 600, 
              width: '100%',
              overflowX: 'auto',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#555',
                },
              },
            }}
          >
            <Box sx={{ minWidth: { xs: '800px', md: '100%' } }}>
              <DataGrid
              rows={filteredUsers}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              loading={isLoading}
              sx={{
                border: 'none',
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #e9ecef',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#495057',
                  height: '56px',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-columnHeaderTitleContainer': {
                    padding: '0 16px',
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f1f3f4',
                  padding: '0 16px',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#495057',
                  height: '72px',
                  minHeight: '72px !important',
                  '& .MuiBox-root': {
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '8px',
                  },
                },
                '& .MuiDataGrid-row': {
                  display: 'flex',
                  alignItems: 'stretch',
                  minHeight: '72px !important',
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafbfc',
                  },
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#e3f2fd !important',
                  transform: 'scale(1.001)',
                  transition: 'all 0.2s ease-in-out',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e9ecef',
                  padding: '16px 20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '64px',
                },
                '& .MuiDataGrid-selectedRowCount': {
                  color: '#3B3B3B',
                  fontWeight: 500,
                },
                '& .MuiTablePagination-root': {
                  color: '#495057',
                },
                '& .MuiCheckbox-root': {
                  color: '#3B3B3B',
                  padding: '8px',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-iconButtonContainer': {
                  visibility: 'visible',
                },
                '& .MuiDataGrid-sortIcon': {
                  opacity: 0.6,
                },
                '& .MuiDataGrid-columnHeader': {
                  '&:focus': {
                    outline: 'none',
                  },
                },
              }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => handleUserAction(selectedUser!, 'activate')}
          disabled={selectedUser?.isActive}
        >
          <CheckCircle sx={{ mr: 1 }} />
          Activate
        </MenuItem>
        <MenuItem
          onClick={() => handleUserAction(selectedUser!, 'deactivate')}
          disabled={!selectedUser?.isActive}
        >
          <Block sx={{ mr: 1 }} />
          Deactivate
        </MenuItem>
        <MenuItem
          onClick={() => handleUserAction(selectedUser!, 'delete')}
          sx={{ color: 'error.main' }}
        >
          <Block sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionType} user "{selectedUser?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            Cancel
          </Button>
          <Button onClick={confirmAction} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
